/** Top consumers list component showing carriers ranked by cost. */
import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Chip,
  LinearProgress,
} from "@mui/material";
import { Link } from "react-router-dom";
import { EmojiEvents } from "@mui/icons-material";

interface TopConsumer {
  rank: number;
  account_id: string;
  name: string;
  total_calls: number;
  total_cost: number;
  avg_duration: number;
}

interface TopConsumersProps {
  consumers: TopConsumer[];
  maxCost?: number; // For calculating relative bar widths
}

/** Format number with commas. */
const formatNumber = (value: number | undefined | null): string => {
  if (value === undefined || value === null) return "0";
  return new Intl.NumberFormat("en-US").format(value);
};

/** Format seconds as "Xm Ys". */
const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
};

/** Get rank badge color. */
const getRankColor = (rank: number): "primary" | "secondary" | "info" | "warning" => {
  switch (rank) {
    case 1:
      return "warning"; // Gold
    case 2:
      return "secondary"; // Silver
    case 3:
      return "primary"; // Bronze
    default:
      return "info";
  }
};

/** Get rank icon. */
const getRankIcon = (rank: number) => {
  if (rank <= 3) {
    return <EmojiEvents fontSize="small" sx={{ mr: 0.5 }} />;
  }
  return null;
};

/**
 * TopConsumers component.
 *
 * Displays a ranked list of carriers by total cost.
 * - Top 3 highlighted with gold/silver/bronze colors
 * - Inline bar chart showing relative costs
 * - Click row to navigate to CDRs page filtered by carrier
 */
const TopConsumers: React.FC<TopConsumersProps> = ({
  consumers,
  maxCost,
}) => {
  // Calculate max cost if not provided
  const calculatedMaxCost =
    maxCost || consumers.length > 0
      ? Math.max(...consumers.map((c) => c.total_cost))
      : 0;

  const isEmpty = consumers.length === 0;

  return (
    <Card elevation={2}>
      <CardContent>
        <Typography variant="h6" component="h2" gutterBottom>
          Top Consumers by Cost
        </Typography>

        {isEmpty ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}
          >
            <Typography variant="body2" color="text.secondary">
              No consumer data for selected period
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={60}>Rank</TableCell>
                  <TableCell>Carrier</TableCell>
                  <TableCell align="right">Calls</TableCell>
                  <TableCell align="right">Cost</TableCell>
                  <TableCell align="right">Avg Duration</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {consumers.map((consumer) => {
                  const barWidth =
                    calculatedMaxCost > 0
                      ? (consumer.total_cost / calculatedMaxCost) * 100
                      : 0;

                  return (
                    <TableRow
                      key={consumer.account_id}
                      hover
                      sx={{ cursor: "pointer" }}
                      component={Link}
                      to={`/cdrs?carrier=${consumer.account_id}`}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          {getRankIcon(consumer.rank)}
                          <Chip
                            label={consumer.rank}
                            size="small"
                            color={getRankColor(consumer.rank)}
                            variant={consumer.rank <= 3 ? "filled" : "outlined"}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {consumer.name}
                          </Typography>
                          {/* Cost bar */}
                          <Box width="100%" mt={0.5}>
                            <LinearProgress
                              variant="determinate"
                              value={barWidth}
                              sx={{
                                height: 4,
                                borderRadius: 2,
                                bgcolor:
                                  consumer.rank <= 3
                                    ? "action.hover"
                                    : "action.disabledBackground",
                              }}
                              color={
                                consumer.rank === 1
                                  ? "warning"
                                  : consumer.rank === 2
                                  ? "secondary"
                                  : consumer.rank === 3
                                  ? "primary"
                                  : "info"
                              }
                            />
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {formatNumber(consumer.total_calls)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color={
                            consumer.rank === 1
                              ? "warning.main"
                              : consumer.rank === 2
                              ? "text.primary"
                              : consumer.rank === 3
                              ? "primary.main"
                              : "text.primary"
                          }
                        >
                          ${formatNumber(parseFloat(consumer.total_cost.toFixed(2)))}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {formatDuration(consumer.avg_duration)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default TopConsumers;
